import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { ModalCenter } from '@shared/components/ui/ModalCenter';
import type { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { useToast } from '@shared/hooks/use-toast';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMapShipmentStatus } from '../../hooks/useMapShipmentStatus';
import type { ShipmentStatus, ShipmentStatusMapping } from '../../models/shipmentStatusMapping.types';
import { ExternalStatusCard } from '../display/ExternalStatusCard';
import { InternalStatusPicker } from '../interaction/InternalStatusPicker';

export interface MapShipmentStatusModalRef {
  open: (mapping: ShipmentStatusMapping) => void;
}

interface MapShipmentStatusModalProps {
  shipmentStatuses: ShipmentStatus[];
  onSuccess?: () => void;
}

export const MapShipmentStatusModal = forwardRef<MapShipmentStatusModalRef, MapShipmentStatusModalProps>(
  ({ shipmentStatuses, onSuccess }, ref) => {
    const { t } = useTranslation('shipmentStatusMappingModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const confirmModalRef = useRef<ConfirmModalRef>(null);
    const { toast } = useToast();
    const { mutateAsync: mapStatus, isPending } = useMapShipmentStatus();
    const [currentMapping, setCurrentMapping] = useState<ShipmentStatusMapping | null>(null);
    const [selectedStatusId, setSelectedStatusId] = useState('none');

    const isMapped = Boolean(currentMapping?.shipment_status);
    const hasChanges =
      currentMapping != null && selectedStatusId !== (currentMapping.shipment_status?.id?.toString() ?? 'none');

    useImperativeHandle(ref, () => ({
      open: (mapping: ShipmentStatusMapping) => {
        setCurrentMapping(mapping);
        setSelectedStatusId(mapping.shipment_status?.id?.toString() ?? 'none');
        modalRef.current?.openModal();
      },
    }));

    const handleClose = () => {
      modalRef.current?.closeModal();
      setCurrentMapping(null);
      setSelectedStatusId('none');
    };

    const handleSave = async () => {
      if (!currentMapping) return;

      try {
        await mapStatus({
          id: currentMapping.id,
          request: {
            shipment_status_id: selectedStatusId === 'none' ? null : Number(selectedStatusId),
          },
        });

        toast({
          title: t('shipmentStatusMapping.messages.saveSuccess'),
        });

        onSuccess?.();
        handleClose();
      } catch (error: any) {
        toast({
          title: t('shipmentStatusMapping.messages.saveError'),
          description: error?.message,
          variant: 'destructive',
        });
      }
    };

    const handleUnmap = () => {
      if (!currentMapping) return;

      confirmModalRef.current?.open(
        async () => {
          try {
            await mapStatus({
              id: currentMapping.id,
              request: { shipment_status_id: null },
            });

            toast({
              title: t('shipmentStatusMapping.messages.unmapSuccess'),
            });

            onSuccess?.();
            handleClose();
          } catch (error: any) {
            toast({
              title: t('shipmentStatusMapping.messages.unmapError'),
              description: error?.message,
              variant: 'destructive',
            });
          }
        },
        undefined,
        t('shipmentStatusMapping.modal.unmap'),
        t('shipmentStatusMapping.modal.unmapConfirm'),
        t('shipmentStatusMapping.modal.unmap'),
        t('shipmentStatusMapping.modal.cancel'),
      );
    };

    const footer = currentMapping ? (
      <div className="flex items-center justify-between gap-3">
        <div>
          {isMapped && (
            <Button
              type="button"
              variant="outline"
              onClick={handleUnmap}
              disabled={isPending}
              className="text-error hover:text-error"
            >
              {t('shipmentStatusMapping.modal.unmap')}
            </Button>
          )}
        </div>
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
            {t('shipmentStatusMapping.modal.cancel')}
          </Button>
          <Button
            type="button"
            variant="default"
            onClick={handleSave}
            disabled={!hasChanges || isPending}
            className="text-white"
          >
            {isPending ? t('shipmentStatusMapping.modal.saving') : t('shipmentStatusMapping.modal.save')}
          </Button>
        </div>
      </div>
    ) : null;

    return (
      <>
        <ModalCenter
          ref={modalRef}
          title={isMapped ? t('shipmentStatusMapping.modal.editTitle') : t('shipmentStatusMapping.modal.title')}
          type="default"
          width="lg"
          footer={footer}
          onCloseModal={() => {
            setCurrentMapping(null);
            setSelectedStatusId('none');
          }}
        >
          {currentMapping && (
            <div className="space-y-6">
              <ExternalStatusCard mapping={currentMapping} />
              <InternalStatusPicker
                shipmentStatuses={shipmentStatuses}
                selectedStatusId={selectedStatusId}
                onSelect={setSelectedStatusId}
                allowNone
                testIdPrefix="map-modal-status"
              />
            </div>
          )}
        </ModalCenter>
        <ConfirmModal ref={confirmModalRef} />
      </>
    );
  },
);

MapShipmentStatusModal.displayName = 'MapShipmentStatusModal';
